// ExamFlow Exams - Unified exam configurations

// 配置加载器（仅 loadExamConfig 需在本文件内使用，其余仅再导出）
import { loadExamConfig } from './configLoader';
export {
  loadExamConfig,
  getExamConfig,
  registerExamConfig,
  clearConfigCache,
  getRegisteredExamIds,
} from './configLoader';

// TCF 考试配置
export { tcfExamConfig } from './exams/tcf';
export * from './exams/tcf';

// IELTS 考试配置
export { ieltsExamConfig } from './exams/ielts';
export {
  IELTS_SECTIONS,
  IELTS_LISTENING_SECTION,
  IELTS_READING_SECTION,
  IELTS_WRITING_SECTION,
  IELTS_SPEAKING_SECTION,
} from './exams/ielts/sections';
export { ieltsScoringRules } from './exams/ielts/scoring';

// TOEFL 考试配置
export { toeflExamConfig } from './exams/toefl';
export {
  TOEFL_SECTIONS,
  TOEFL_READING_SECTION,
  TOEFL_LISTENING_SECTION,
  TOEFL_SPEAKING_SECTION,
  TOEFL_WRITING_SECTION,
} from './exams/toefl/sections';
export { toeflScoringRules } from './exams/toefl/scoring';

// DELE 考试配置
export { deleExamConfig } from './exams/dele';
export {
  DELE_SECTIONS,
  DELE_READING_SECTION,
  DELE_LISTENING_SECTION,
  DELE_WRITING_SECTION,
  DELE_SPEAKING_SECTION,
} from './exams/dele/sections';
export { deleScoringRules } from './exams/dele/scoring';

// SIELE 考试配置
export { sieleExamConfig } from './exams/siele';
export {
  SIELE_SECTIONS,
  SIELE_READING_SECTION,
  SIELE_LISTENING_SECTION,
  SIELE_WRITING_SECTION,
  SIELE_SPEAKING_SECTION,
} from './exams/siele/sections';
export { sieleScoringRules } from './exams/siele/scoring';

// JLPT 考试配置
export { jlptExamConfig } from './exams/jlpt';
export {
  JLPT_SECTIONS,
  JLPT_VOCABULARY_SECTION,
  JLPT_GRAMMAR_READING_SECTION,
  JLPT_LISTENING_SECTION,
} from './exams/jlpt/sections';
export { jlptScoringRules } from './exams/jlpt/scoring';

// TOPIK 考试配置
export { topikExamConfig } from './exams/topik';
export {
  TOPIK_SECTIONS,
  TOPIK_LISTENING_SECTION,
  TOPIK_READING_SECTION,
  TOPIK_WRITING_SECTION,
} from './exams/topik/sections';
export { topikScoringRules } from './exams/topik/scoring';

// 按考试类型的题目难度选项（生题、导入等）
export {
  getSupportedLevelsForExam,
  getGenerationLevelsForExam,
  getLevelOptionsForExam,
  getLevelOptionsForGeneration,
  isLevelSupportedForExam,
} from './levelOptions';

// AI 生产内容主题目录与轮换（防题材重复；非 Platform Scope）
export {
  getContentTopicsForScope,
  getContentTopicOptionsForScope,
  isContentTopicSupported,
  normalizeOptionalContentTopicId,
  normalizeTopicsForScope,
  type ContentTopic,
} from './topicCatalog';
export {
  aggregateTopicCoverage,
  buildTopicAdvisorSummary,
  generationFocusTopicLimit,
  planGenerationTopics,
  type GenerationTopicPlan,
  type TopicAdvisorLabel,
  type TopicAdvisorSummary,
  type TopicCoverageCounts,
} from './topicRotation';

// Open-response writing rubrics (Training Session AI grade — not closed ScoringRules)
export {
  DEFAULT_WRITING_RUBRIC,
  IELTS_WRITING_RUBRIC,
  TCF_WRITING_RUBRIC,
  TOEFL_WRITING_RUBRIC,
  getWritingRubric,
  normalizeWritingPerformancePercent,
  type WritingRubric,
  type WritingRubricAggregate,
  type WritingRubricDimensionDef,
  type WritingRubricDimensionId,
  type WritingRubricLevelHintStyle,
} from './writingRubric';

export {
  getWritingTaskOptionsForExam,
  getWritingSuggestedMinutes,
  isWritingTaskSupportedForExam,
  normalizeOptionalWritingTaskNumber,
  type WritingTaskOption,
} from './writingTaskOptions';

// Writing Training「常用表达」脚手架（按 languageId；非 Exam 键）
export {
  WRITING_EXPRESSION_CATEGORY_IDS,
  WRITING_EXPRESSION_CATEGORY_LABELS,
  getWritingExpressionLanguageIds,
  getWritingExpressionsForLanguage,
  hasWritingExpressionPack,
  isWritingExpressionCategoryId,
  type WritingExpressionCategoryId,
  type WritingExpressionItem,
} from './writingExpressionBank';

// Skill / section 运营侧展示名约定
export { isOperatorFacingSectionName } from './sectionDisplayName';

// 注册所有考试配置（确保可以直接使用）
import { registerExamConfig } from './configLoader';
import { tcfExamConfig } from './exams/tcf';
import { ieltsExamConfig } from './exams/ielts';
import { toeflExamConfig } from './exams/toefl';
import { deleExamConfig } from './exams/dele';
import { sieleExamConfig } from './exams/siele';
import { jlptExamConfig } from './exams/jlpt';
import { topikExamConfig } from './exams/topik';

registerExamConfig('tcf', tcfExamConfig);
registerExamConfig('ielts', ieltsExamConfig);
registerExamConfig('toefl', toeflExamConfig);
registerExamConfig('dele', deleExamConfig);
registerExamConfig('siele', sieleExamConfig);
registerExamConfig('jlpt', jlptExamConfig);
registerExamConfig('topik', topikExamConfig);

/**
 * Section 介绍文案接口
 */
export interface SectionIntro {
  sectionId: string;
  title: string;
  description: string;
  tips?: string[];
  duration?: number;
}

// Section 介绍文案加载器（静态映射，避免动态 import 兼容性问题）
import { getTCFSectionIntro } from './exams/tcf/intro';
import { getIELTSSectionIntro } from './exams/ielts/intro';
import { getTOEFLSectionIntro } from './exams/toefl/intro';
import { getDELESectionIntro } from './exams/dele/intro';
import { getSIELESectionIntro } from './exams/siele/intro';
import { getJLPTSectionIntro } from './exams/jlpt/intro';
import { getTOPIKSectionIntro } from './exams/topik/intro';

const sectionIntroLoaders: Record<string, (sectionId: string) => SectionIntro | null> = {
  tcf: getTCFSectionIntro,
  ielts: getIELTSSectionIntro,
  toefl: getTOEFLSectionIntro,
  dele: getDELESectionIntro,
  siele: getSIELESectionIntro,
  jlpt: getJLPTSectionIntro,
  topik: getTOPIKSectionIntro,
};

/**
 * 加载 Section 介绍文案
 * @param examType 考试类型（如 'tcf', 'ielts'）
 * @param sectionId Section ID（如 'listening', 'reading'）
 * @returns Section 介绍文案，如果不存在则返回 null
 */
export async function loadSectionIntro(
  examType: string,
  sectionId: string
): Promise<SectionIntro | null> {
  const loader = sectionIntroLoaders[examType];
  if (!loader) {
    return null;
  }
  try {
    return loader(sectionId);
  } catch (error) {
    console.warn(`Failed to load section intro for ${examType}/${sectionId}:`, error);
    return null;
  }
}

// ==================== 题目类型约束工具函数 ====================

import type {
  ExamTypeConfig,
  ExamSection,
  SectionQuestionTypeConstraint,
  ExamSpecificQuestionTypeDefinition,
} from '@examflow/core';

/**
 * 从考试配置获取题目类型定义
 */
export function getQuestionTypesFromExamConfig(
  config: ExamTypeConfig
): Record<string, ExamSpecificQuestionTypeDefinition> {
  return config.questionTypeDefinitions ?? {};
}

/**
 * 获取 Section 的题目类型约束
 * 优先返回新的 questionTypeConstraints，如果没有则返回旧的 questionTypes 转换后的约束
 */
export function getSectionQuestionTypeConstraints(
  section: ExamSection
): SectionQuestionTypeConstraint[] {
  // 如果有新的约束配置，直接返回
  if (section.questionTypeConstraints && section.questionTypeConstraints.length > 0) {
    return section.questionTypeConstraints;
  }

  // 否则从旧的 questionTypes 转换（向后兼容）
  if (section.questionTypes && section.questionTypes.length > 0) {
    return section.questionTypes.map((type) => ({
      questionTypeId: type,
    }));
  }

  return [];
}

/**
 * 检查题目类型是否被 Section 支持
 */
export function isQuestionTypeSupportedInSection(
  section: ExamSection,
  questionTypeId: string
): boolean {
  const constraints = getSectionQuestionTypeConstraints(section);
  return constraints.some((c) => c.questionTypeId === questionTypeId);
}

/**
 * 获取 Section 中某类型的数量限制
 */
export function getSectionQuestionCountLimit(
  section: ExamSection,
  questionTypeId: string
): { min: number; max: number } | undefined {
  const constraints = getSectionQuestionTypeConstraints(section);
  const constraint = constraints.find((c) => c.questionTypeId === questionTypeId);
  return constraint?.countLimit;
}

/**
 * 根据考试类型与 section 解析该 section 的默认题型列表（用于 AI 生题未选题型时）
 * 返回与 AI 包一致的作答形态 id：single_choice, multiple_choice 等
 */
export async function getSectionDefaultQuestionTypes(
  examType: string,
  sectionId: string
): Promise<string[]> {
  const config = await loadExamConfig(examType);
  const section = config.sections?.find((s: ExamSection) => s.id === sectionId);
  if (!section) return [];

  const constraints = getSectionQuestionTypeConstraints(section);
  if (constraints.length === 0) return [];

  const definitions = config.questionTypeDefinitions ?? {};
  const knownBaseTypes = [
    'single_choice',
    'multiple_choice',
    'true_false',
    'fill_blank',
  ];
  const baseTypes: string[] = [];
  for (const c of constraints) {
    const def = definitions[c.questionTypeId];
    const baseType =
      def?.baseType != null
        ? (def.baseType as string)
        : knownBaseTypes.includes(c.questionTypeId)
          ? c.questionTypeId
          : null;
    if (baseType) baseTypes.push(baseType);
  }
  return [...new Set(baseTypes)];
}

/**
 * 根据考试类型获取所有 section 的默认题型并集（用于 AI 生题未选 section 时的 fallback）
 * 返回与 AI 包一致的作答形态 id：single_choice, multiple_choice 等
 */
export async function getExamDefaultQuestionTypes(examType: string): Promise<string[]> {
  const config = await loadExamConfig(examType);
  if (!config.sections?.length) return [];
  const allTypes = new Set<string>();
  const definitions = config.questionTypeDefinitions ?? {};
  const knownBaseTypes = [
    'single_choice',
    'multiple_choice',
    'true_false',
    'fill_blank',
  ];
  for (const section of config.sections) {
    const constraints = getSectionQuestionTypeConstraints(section);
    for (const c of constraints) {
      const def = definitions[c.questionTypeId];
      const baseType =
        def?.baseType != null
          ? (def.baseType as string)
          : knownBaseTypes.includes(c.questionTypeId)
            ? c.questionTypeId
            : null;
      if (baseType) allTypes.add(baseType);
    }
  }
  return [...allTypes];
}

/**
 * 根据考试类型与 sectionId 获取 section 展示名称（用于 prompt 中的 sectionDesc）
 */
export async function getSectionName(
  examType: string,
  sectionId: string
): Promise<string | undefined> {
  const config = await loadExamConfig(examType);
  return config.sections?.find((s: ExamSection) => s.id === sectionId)?.name;
}

/**
 * 获取考试配置中的题目类型定义
 */
export function getQuestionTypeDefinitionFromConfig(
  config: ExamTypeConfig,
  typeId: string
): ExamSpecificQuestionTypeDefinition | undefined {
  return config.questionTypeDefinitions?.[typeId];
}

/**
 * 根据基础类型获取考试配置中的题目类型定义
 */
export function getQuestionTypeDefinitionByBaseType(
  config: ExamTypeConfig,
  baseType: string
): ExamSpecificQuestionTypeDefinition | undefined {
  const definitions = Object.values(config.questionTypeDefinitions ?? {});
  return definitions.find((def) => def.baseType === baseType);
}

/**
 * 验证题目是否符合考试配置的约束
 */
export function validateQuestionAgainstExamConfig(
  question: any,
  config: ExamTypeConfig,
  sectionId?: string
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // 1. 验证题目类型是否存在
  const typeId = question.examSpecificTypeId ?? question.type;
  const typeDef = getQuestionTypeDefinitionFromConfig(config, typeId);

  if (!typeDef) {
    // 如果没有考试特定定义，检查是否是基础类型
    const baseTypeDef = getQuestionTypeDefinitionByBaseType(config, question.type);
    if (!baseTypeDef) {
      errors.push(`题目类型 ${question.type} 不在考试 ${config.id} 的支持列表中`);
      return { valid: false, errors };
    }
  }

  // 2. 如果有指定 section，验证 section 约束
  if (sectionId) {
    const section = config.sections.find((s) => s.id === sectionId);
    if (!section) {
      errors.push(`Section ${sectionId} 不存在于考试 ${config.id}`);
      return { valid: false, errors };
    }

    const isSupported = isQuestionTypeSupportedInSection(section, typeId);
    if (!isSupported) {
      errors.push(`题目类型 ${typeId} 不被 Section ${sectionId} 支持`);
    }
  }

  return { valid: errors.length === 0, errors };
}

export {
  resolveExamQuestionTypeId,
  validateQuestionsForScope,
  type QuestionScopeInput,
} from './validateQuestionsForScope';

export {
  validateIeltsReadingQuestionFormat,
  validateToeflReadingQuestionFormat,
} from './questionFormatValidators';

